import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { PermissionGate } from './ui/PermissionGate';
import { 
  Plus, 
  Search, 
  Filter, 
  FileSpreadsheet, 
  Eye, 
  Edit,
  Trash2,
  Package, 
  Package2,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Loader2,
  FileText,
  IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  packingApiService, 
  PackingHeader, 
  PackingStatus, 
  PackingHeaderFilters,
  PackingDetail
} from '../services/packingApi';
import { formatDate } from '../utils/dateUtils';
import { useBranch } from '../contexts/BranchContext';

// Helper function to format date for date input (YYYY-MM-DD)
const formatDateForInput = (dateString: string | undefined | null): string => {
  if (!dateString || dateString.trim() === '') return '';
  
  try {
    // Trim whitespace
    const trimmed = dateString.trim();
    
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    
    // If it contains 'T', split and take the date part (handles ISO strings)
    if (trimmed.includes('T')) {
      const datePart = trimmed.split('T')[0];
      // Verify it's in correct format
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
    }
    
    // If it's in DD/MM/YYYY or DD-MM-YYYY format, convert it
    const ddmmyyyyMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, day, month, year] = ddmmyyyyMatch;
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = month.padStart(2, '0');
      return `${year}-${paddedMonth}-${paddedDay}`;
    }
    
    // Try to parse as Date and format (handles various formats)
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    console.warn('Could not parse date string:', dateString);
    return '';
  } catch (error) {
    console.error('Error formatting date:', error, 'Input:', dateString);
    return '';
  }
};

const PackingEntriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const formatUpper = (value?: string) => (value ? value.toUpperCase() : 'N/A');
  
  // State management
  const [filteredHeaders, setFilteredHeaders] = useState<PackingHeader[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(''); // query actually used for current results (set on Search click)
  const [statusFilter, setStatusFilter] = useState<PackingStatus | 'all'>('all');
  
  // Selection state
  const [selectedEntries, setSelectedEntries] = useState<Set<number>>(new Set());
  
  // Invoice dialog state (for individual boxes)
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [selectedPackingHeaderId, setSelectedPackingHeaderId] = useState<number | null>(null);
  const [packingDetails, setPackingDetails] = useState<PackingDetail[]>([]);
  const [isLoadingPackingDetails, setIsLoadingPackingDetails] = useState(false);
  const [selectedPackingDetails, setSelectedPackingDetails] = useState<Set<number>>(new Set());
  const [boxesToInvoiceCount, setBoxesToInvoiceCount] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [invoiceDate, setInvoiceDate] = useState<string>(new Date().toISOString().split('T')[0]);

  // Header invoice dialog state (for whole packing headers)
  const [isHeaderInvoiceDialogOpen, setIsHeaderInvoiceDialogOpen] = useState(false);
  const [headerInvoiceNumber, setHeaderInvoiceNumber] = useState<string>('');
  const [headerInvoiceDate, setHeaderInvoiceDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const PACKING_ROUTE = '/packing-entries';
  
  // Statistics state - commented out for now
  // const [stats, setStats] = useState({
  //   total: 0,
  //   pending: 0,
  //   inProgress: 0,
  //   completed: 0,
  //   cancelled: 0,
  //   totalQuantity: 0,
  //   totalBoxes: 0
  // });

  // Load packing headers (stats commented out for now)
  const loadData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const filters: PackingHeaderFilters = {};
      if (selectedBranch) {
        filters.branchId = selectedBranch;
      }
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      const headersResponse = appliedSearchQuery.trim()
        ? await packingApiService.searchPackingHeaders(appliedSearchQuery, currentPage, pageSize, filters)
        : await packingApiService.getPackingHeaders(currentPage, pageSize, filters);

      if (headersResponse.success) {
        // Map API response to ensure rejectQuantity is properly set (handle both camelCase and snake_case)
        const mappedHeaders = Array.isArray(headersResponse.data)
          ? headersResponse.data.map((header: any) => ({
              ...header,
              rejectQuantity: header.rejectQuantity ?? header.reject_quantity ?? 0
            }))
          : [];
        setFilteredHeaders(mappedHeaders);

        if (headersResponse.pagination) {
          setTotalPages(headersResponse.pagination.totalPages);
          setTotalCount(headersResponse.pagination.totalCount);
        }
      }
    } catch (error) {
      toast.error('Failed to load packing headers');
    } finally {
      setIsLoadingData(false);
    }
  }, [currentPage, pageSize, selectedBranch, statusFilter, appliedSearchQuery]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle search: apply the current search query and reset to page 1; loadData (useEffect) will run and fetch
  const handleSearch = useCallback(() => {
    setAppliedSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  }, [searchQuery]);

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle export
  const handleExport = async () => {
    try {
      const filters: PackingHeaderFilters = {};
      
      // Add branchId to filters
      if (selectedBranch) {
        filters.branchId = selectedBranch;
      }
      
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      const blob = await packingApiService.exportPackingHeaders(filters, 'csv');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `packing-headers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completed successfully!');
    } catch (error) {
      // console.error('Error exporting packing headers:', error);
      toast.error('Failed to export packing headers');
    }
  };

  // Handle view packing header (prefer batch view when available)
  const handleViewPackingHeader = (header: PackingHeader) => {
    if (header.batchId) {
      navigate(`/packing-entries/batch/${header.batchId}`);
      return;
    }
    navigate(`/packing-entries/${header.id}`);
  };

  const handleEditBoxes = (header: PackingHeader) => {
    if (!header.batchId) {
      toast.error('Batch ID not available for this packing entry');
      return;
    }
    navigate(`/packing-entries/batch/${header.batchId}/boxes-edit`);
  };

  // Handle delete packing header (only when PACKED)
  const handleDeletePackingHeader = async (header: PackingHeader) => {
    if (header.status !== PackingStatus.PACKED) {
      return;
    }
    if (!window.confirm(`Delete packing header ${header.packingHeaderNo || header.id}?`)) {
      return;
    }
    try {
      if (header.batchId) {
        await packingApiService.deletePackingHeadersByBatchId(header.batchId);
        setFilteredHeaders(prev => prev.filter(item => item.batchId !== header.batchId));
      } else {
        await packingApiService.deletePackingHeader(String(header.id));
        setFilteredHeaders(prev => prev.filter(item => item.id !== header.id));
      }
      setSelectedEntries(prev => {
        const next = new Set(prev);
        if (header.batchId) {
          filteredHeaders.forEach(item => {
            if (item.batchId === header.batchId) {
              next.delete(item.id);
            }
          });
        } else {
          next.delete(header.id);
        }
        return next;
      });
    } catch (error) {
      // toasted in service
    }
  };


  // Get non-invoiced headers
  const getNonInvoicedHeaders = (): PackingHeader[] => {
    return filteredHeaders.filter(header => header.status !== PackingStatus.INVOICED);
  };

  // Handle checkbox selection
  const handleSelectEntry = (id: number, checked: boolean) => {
    // Don't allow selection of invoiced entries
    const header = filteredHeaders.find(h => h.id === id);
    if (header?.status === PackingStatus.INVOICED) {
      return;
    }

    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  // Handle select all (only non-invoiced entries)
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const nonInvoicedHeaders = getNonInvoicedHeaders();
      setSelectedEntries(new Set(nonInvoicedHeaders.map(header => header.id)));
    } else {
      setSelectedEntries(new Set());
    }
  };

  // Check if all non-invoiced entries are selected
  const nonInvoicedHeaders = getNonInvoicedHeaders();
  const isAllSelected = nonInvoicedHeaders.length > 0 && nonInvoicedHeaders.every(header => selectedEntries.has(header.id));

  // Handle mark as invoiced - opens dialog
  const handleMarkAsInvoiced = () => {
    if (selectedEntries.size === 0) {
      toast.error('Please select at least one packing entry');
      return;
    }

    // Check if any selected header has invoice info
    const selectedHeaders = filteredHeaders.filter(h => selectedEntries.has(h.id));
    const headerWithInvoice = selectedHeaders.find(h => h.invoiceNumber || h.invoiceDate);
    
    // Pre-populate invoice fields if available from response, otherwise don't set
    if (headerWithInvoice?.invoiceNumber) {
      setHeaderInvoiceNumber(headerWithInvoice.invoiceNumber);
    } else {
      setHeaderInvoiceNumber('');
    }
    
    if (headerWithInvoice?.invoiceDate) {
      setHeaderInvoiceDate(formatDateForInput(headerWithInvoice.invoiceDate));
    } else {
      // Don't set if not in response
      setHeaderInvoiceDate('');
    }

    // Open dialog for invoice details
    setIsHeaderInvoiceDialogOpen(true);
  };

  // Handle confirm mark as invoiced with invoice details
  const handleConfirmMarkAsInvoiced = async () => {
    if (selectedEntries.size === 0) {
      toast.error('Please select at least one packing entry');
      return;
    }

    if (!headerInvoiceNumber || headerInvoiceNumber.trim() === '') {
      toast.error('Please enter an invoice number');
      return;
    }

    if (!headerInvoiceDate) {
      toast.error('Please select an invoice date');
      return;
    }

    const packingHeaderIds = Array.from(selectedEntries);
    
    try {
      setIsLoading(true);
      await packingApiService.markPackingDetailsAsInvoiced(packingHeaderIds, headerInvoiceNumber.trim(), headerInvoiceDate);
      setIsHeaderInvoiceDialogOpen(false);
      setSelectedEntries(new Set()); // Clear selection
      setHeaderInvoiceNumber('');
      setHeaderInvoiceDate('');
      loadData(); // Reload data to reflect changes
    } catch (error) {
      // console.error('Error marking packing details as invoiced:', error);
      // Error toast is already shown in the API service
    } finally {
      setIsLoading(false);
    }
  };

  // Handle invoice button click
  const handleInvoiceClick = async (packingHeaderId: number) => {
    setSelectedPackingHeaderId(packingHeaderId);
    setIsInvoiceDialogOpen(true);
    setIsLoadingPackingDetails(true);
    setSelectedPackingDetails(new Set()); // Clear previous selections
    setBoxesToInvoiceCount(''); // Clear input
    
    try {
      const response = await packingApiService.getPackingDetailsByHeader(packingHeaderId.toString());
      if (response.success) {
        const details = response.data || [];
        setPackingDetails(details);
        
        // Extract invoice number and date from response if available
        // Check if any detail has invoice info, or check the header
        const firstDetailWithInvoice = details.find(d => d.invoiceNumber || d.invoiceDate);
        const header = filteredHeaders.find(h => h.id === packingHeaderId);
        
        // Debug: Log what we received
        if (firstDetailWithInvoice) {
          console.log('Invoice info from detail:', {
            invoiceNumber: firstDetailWithInvoice.invoiceNumber,
            invoiceDate: firstDetailWithInvoice.invoiceDate,
            rawDate: firstDetailWithInvoice.invoiceDate
          });
        }
        if (header) {
          console.log('Invoice info from header:', {
            invoiceNumber: header.invoiceNumber,
            invoiceDate: header.invoiceDate,
            rawDate: header.invoiceDate
          });
        }
        
        // Use invoice info from detail or header, if available
        if (firstDetailWithInvoice?.invoiceNumber) {
          setInvoiceNumber(firstDetailWithInvoice.invoiceNumber);
        } else if (header?.invoiceNumber) {
          setInvoiceNumber(header.invoiceNumber);
        } else {
          setInvoiceNumber(''); // Don't set if not in response
        }
        
        // Try to get invoice date from detail first, then header
        let invoiceDateValue = '';
        if (firstDetailWithInvoice?.invoiceDate) {
          invoiceDateValue = formatDateForInput(firstDetailWithInvoice.invoiceDate);
          console.log('Formatted invoice date from detail:', invoiceDateValue);
        } else if (header?.invoiceDate) {
          invoiceDateValue = formatDateForInput(header.invoiceDate);
          console.log('Formatted invoice date from header:', invoiceDateValue);
        }
        setInvoiceDate(invoiceDateValue);
      } else {
        toast.error('Failed to load packing details');
        setPackingDetails([]);
        setInvoiceNumber('');
        setInvoiceDate('');
      }
    } catch (error) {
      // console.error('Error loading packing details:', error);
      toast.error('Failed to load packing details');
      setPackingDetails([]);
      setInvoiceNumber('');
      setInvoiceDate('');
    } finally {
      setIsLoadingPackingDetails(false);
    }
  };

  // Handle auto-select boxes based on count
  const handleAutoSelectBoxes = (count: string) => {
    setBoxesToInvoiceCount(count);
    
    const numCount = parseInt(count, 10);
    if (isNaN(numCount) || numCount <= 0) {
      setSelectedPackingDetails(new Set());
      return;
    }

    const nonInvoiced = getNonInvoicedPackingDetails();
    const maxCount = nonInvoiced.length;
    
    if (numCount > maxCount) {
      toast.error(`Cannot select more than ${maxCount} non-invoiced boxes`);
      setBoxesToInvoiceCount(maxCount.toString());
      setSelectedPackingDetails(new Set(nonInvoiced.slice(0, maxCount).map(detail => detail.id)));
      return;
    }

    // Auto-select first N non-invoiced boxes
    const boxesToSelect = nonInvoiced.slice(0, numCount);
    setSelectedPackingDetails(new Set(boxesToSelect.map(detail => detail.id)));
  };

  // Get non-invoiced packing details
  const getNonInvoicedPackingDetails = (): PackingDetail[] => {
    return packingDetails.filter(detail => !detail.isInvoiced);
  };

  // Handle packing detail checkbox selection
  const handleSelectPackingDetail = (id: number, checked: boolean) => {
    // Don't allow selection of invoiced boxes
    const detail = packingDetails.find(d => d.id === id);
    if (detail?.isInvoiced) {
      return;
    }

    setSelectedPackingDetails(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  // Handle select all packing details (only non-invoiced ones)
  const handleSelectAllPackingDetails = (checked: boolean) => {
    if (checked) {
      const nonInvoicedDetails = getNonInvoicedPackingDetails();
      setSelectedPackingDetails(new Set(nonInvoicedDetails.map(detail => detail.id)));
    } else {
      setSelectedPackingDetails(new Set());
    }
  };

  // Check if all non-invoiced packing details are selected
  const nonInvoicedDetails = getNonInvoicedPackingDetails();
  const isAllPackingDetailsSelected = nonInvoicedDetails.length > 0 && nonInvoicedDetails.every(detail => selectedPackingDetails.has(detail.id));

  // Handle mark packing details as invoiced from dialog
  const handleMarkPackingDetailsAsInvoiced = async () => {
    if (selectedPackingDetails.size === 0) {
      toast.error('Please select at least one box to mark as invoiced');
      return;
    }

    if (!invoiceNumber || invoiceNumber.trim() === '') {
      toast.error('Please enter an invoice number');
      return;
    }

    if (!invoiceDate) {
      toast.error('Please select an invoice date');
      return;
    }

    // Get box numbers from selected packing details
    const selectedBoxNumbers = packingDetails
      .filter(detail => selectedPackingDetails.has(detail.id))
      .map(detail => detail.boxNumber);

    if (selectedBoxNumbers.length === 0) {
      toast.error('No valid box numbers found');
      return;
    }

    try {
      setIsLoadingPackingDetails(true);
      await packingApiService.markPackingDetailsAsInvoicedByBoxNumbers(selectedBoxNumbers, invoiceNumber.trim(), invoiceDate);
      setIsInvoiceDialogOpen(false);
      setSelectedPackingDetails(new Set());
      setInvoiceNumber('');
      setInvoiceDate(new Date().toISOString().split('T')[0]);
      loadData(); // Reload data to reflect changes
    } catch (error) {
      // console.error('Error marking packing details as invoiced:', error);
      // Error toast is already shown in the API service
    } finally {
      setIsLoadingPackingDetails(false);
    }
  };

  // Get status icon
  const getStatusIcon = (status: PackingStatus) => {
    switch (status) {
      case PackingStatus.PACKED:
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case PackingStatus.INVOICED:
        return <FileText className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  // Get status badge variant and color
  const getStatusBadgeVariant = (status: PackingStatus | string) => {
    const statusUpper = status?.toString().toUpperCase();
    switch (statusUpper) {
      case PackingStatus.PACKED:
      case 'PACKED':
        return 'default';
      case PackingStatus.INVOICED:
      case 'INVOICED':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Get status badge className for custom colors
  const getStatusBadgeClassName = (status: PackingStatus | string) => {
    const statusUpper = status?.toString().toUpperCase();
    switch (statusUpper) {
      case PackingStatus.PACKED:
      case 'PACKED':
        return 'bg-green-600 hover:bg-green-700 text-white border-green-600';
      case PackingStatus.INVOICED:
      case 'INVOICED':
        return 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600';
      default:
        return 'bg-gray-600 hover:bg-gray-700 text-white border-gray-600';
    }
  };

  if (isLoadingData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-lg">Loading packing headers...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Packing Headers</h1>
          <p className="text-gray-600 mt-1">Manage packing operations for work orders</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => navigate('/packing-entries/new')}
            className="bg-primary-600 hover:bg-primary-700 text-white shadow-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Packing Header
          </Button>
          <Button 
            variant="outline"
            disabled={selectedEntries.size === 0 || isLoading}
            onClick={handleMarkAsInvoiced}
            className="border-blue-600 text-blue-600 hover:bg-blue-50 hover:text-blue-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-blue-600"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Invoiced
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Statistics Cards - Commented out for now */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Package className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Headers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Package2 className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Boxes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalBoxes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Filters */}
      <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
          <CardTitle className="flex items-center text-primary-800 text-lg font-semibold">
            <Filter className="h-5 w-5 mr-3 text-primary-600" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Search packing headers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as PackingStatus | 'all')}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value={PackingStatus.PACKED}>PACKED</SelectItem>
                  <SelectItem value={PackingStatus.INVOICED}>INVOICED</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div className="flex items-end space-x-2">
              <Button 
                onClick={handleSearch} 
                disabled={isLoadingData}
                className="flex-1"
              >
                {isLoadingData ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Search
              </Button>
              <Button 
                onClick={handleExport}
                variant="outline"
                className="flex-1"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
          <CardTitle className="flex items-center justify-between text-primary-800 text-lg font-semibold">
            <div className="flex items-center">
              <Package className="h-5 w-5 mr-3 text-primary-600" />
              Packing Headers ({totalCount})
            </div>
            <span className="text-sm font-medium text-primary-700">
              Showing {filteredHeaders.length} of {totalCount} results
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-6 pb-6">
          {filteredHeaders.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                    <TableHead className="font-semibold w-12">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        disabled={nonInvoicedHeaders.length === 0}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead className="font-semibold">Batch ID</TableHead>
                    <TableHead className="font-semibold w-[260px] min-w-[260px] max-w-[260px]">Work Order</TableHead>
                    <TableHead className="font-semibold">End Good</TableHead>
                    <TableHead className="font-semibold">Quantity</TableHead>
                    <TableHead className="font-semibold">Total Boxes</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Packed On</TableHead>
                    <TableHead className="font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHeaders.map((header, index) => {
                    const isInvoiced = header.status === PackingStatus.INVOICED;
                    return (
                      <TableRow 
                        key={header.id}
                        className={`hover:bg-primary-50/50 ${isInvoiced ? 'opacity-60' : ''}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedEntries.has(header.id)}
                            onCheckedChange={(checked) => handleSelectEntry(header.id, checked as boolean)}
                            disabled={isInvoiced}
                            aria-label={`Select batch ${header.batchId || 'N/A'}`}
                          />
                        </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">{header.batchId ?? 'N/A'}</div>
                      </TableCell>
                      <TableCell className="w-[260px] min-w-[260px] max-w-[260px]">
                        <div
                          className="font-medium text-gray-900 truncate"
                          title={header.workOrderNumber ? formatUpper(header.workOrderNumber) : (header.workOrderId ? `WO-${header.workOrderId}` : 'N/A')}
                        >
                          {header.workOrderNumber ? formatUpper(header.workOrderNumber) : (header.workOrderId ? `WO-${header.workOrderId}` : 'N/A')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">
                          {header.endgoodName ? formatUpper(header.endgoodName) : 'N/A'}
                          {header.endgoodSku && (
                            <div className="text-xs text-gray-500 mt-1">{formatUpper(header.endgoodSku)}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">{header.quantity}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">{header.totalBoxes ?? 'N/A'}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadgeClassName(header.status || '')} border-0 font-semibold`}>
                          {header.status || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">
                          {header.packedOn ? formatDate(header.packedOn) : 'N/A'}
                        </div>
                      </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewPackingHeader(header)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>

                            <PermissionGate route={PACKING_ROUTE} action="edit">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditBoxes(header)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Edit box quantities"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </PermissionGate>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleInvoiceClick(header.id)}
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              title="View Invoice Details"
                            >
                              <IndianRupee className="h-4 w-4" />
                            </Button>
                            <PermissionGate route={PACKING_ROUTE} action="delete">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePackingHeader(header)}
                                disabled={header.status !== PackingStatus.PACKED}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:text-gray-400 disabled:hover:bg-transparent"
                                title={header.status === PackingStatus.PACKED ? 'Delete packing header' : 'Only PACKED headers can be deleted'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </PermissionGate>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200 m-6">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium mb-2">No Packing Headers Found</p>
              <p className="text-sm mb-4">Try adjusting your search criteria or create a new packing header.</p>
              <Button
                onClick={() => navigate('/packing-entries/new')}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Packing Header
              </Button>
            </div>
          )}

        {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 px-6 pb-6">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-primary-200 text-primary-700 hover:bg-primary-50 hover:border-primary-300"
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-primary-200 text-primary-700 hover:bg-primary-50 hover:border-primary-300"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Dialog */}
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-primary-800 text-lg font-semibold">
              <div className="flex items-center">
                <Package className="h-5 w-5 mr-3 text-primary-600" />
                Packing Details
              </div>
              <div className="flex items-center gap-3">
                {packingDetails && packingDetails.length > 0 && (
                  <>
                    <span className="text-sm font-medium bg-primary-100 px-3 py-1 rounded-full text-primary-700">
                      Total Boxes: {packingDetails.length}
                    </span>
                    <span className="text-sm font-medium bg-blue-100 px-3 py-1 rounded-full text-blue-700">
                      Not Invoiced: {nonInvoicedDetails.length}
                    </span>
                  </>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {/* Invoice Information */}
          <div className="mt-4 space-y-4">
            {/* Auto-select input */}
            {packingDetails && packingDetails.length > 0 && nonInvoicedDetails.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-4">
                  <Label htmlFor="boxesToInvoice" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Number of boxes to invoice:
                  </Label>
                  <Input
                    id="boxesToInvoice"
                    type="number"
                    min="0"
                    max={nonInvoicedDetails.length}
                    value={boxesToInvoiceCount}
                    onChange={(e) => handleAutoSelectBoxes(e.target.value)}
                    placeholder={`Max: ${nonInvoicedDetails.length}`}
                    className="w-32"
                  />
                  <span className="text-sm text-gray-500">
                    (Auto-selects first {nonInvoicedDetails.length > 0 ? nonInvoicedDetails.length : 0} available boxes)
                  </span>
                </div>
              </div>
            )}

            {/* Invoice Number and Date */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invoiceNumber" className="text-sm font-medium text-gray-700">
                    Invoice Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="invoiceNumber"
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Enter invoice number"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="invoiceDate" className="text-sm font-medium text-gray-700">
                    Invoice Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="invoiceDate"
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          
          {isLoadingPackingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-lg">Loading packing details...</span>
            </div>
          ) : packingDetails && packingDetails.length > 0 ? (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-primary-50 border-b-2 border-primary-200">
                    <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide w-12">
                      <Checkbox
                        checked={isAllPackingDetailsSelected}
                        onCheckedChange={handleSelectAllPackingDetails}
                        disabled={nonInvoicedDetails.length === 0}
                        aria-label="Select all packing details"
                      />
                    </th>
                    <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Box Number</th>
                    <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Quantity</th>
                    <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {packingDetails.map((detail, index) => {
                    const isInvoiced = detail.isInvoiced || false;
                    return (
                      <tr 
                        key={detail.id} 
                        className={`border-b border-primary-100 hover:bg-primary-50 transition-colors duration-200 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-primary-25'
                        } ${isInvoiced ? 'opacity-60' : ''}`}
                      >
                        <td className="py-3 px-4">
                          <Checkbox
                            checked={selectedPackingDetails.has(detail.id)}
                            onCheckedChange={(checked) => handleSelectPackingDetail(detail.id, checked as boolean)}
                            disabled={isInvoiced}
                            aria-label={`Select ${detail.boxNumber}`}
                          />
                        </td>
                        <td className={`py-3 px-4 font-semibold text-base ${isInvoiced ? 'text-gray-500' : 'text-gray-900'}`}>
                          {detail.boxNumber}
                          {isInvoiced && (
                            <Badge className="ml-2 bg-blue-600 text-white text-xs">Invoiced</Badge>
                          )}
                        </td>
                        <td className={`py-3 px-4 font-medium text-base ${isInvoiced ? 'text-gray-500' : 'text-gray-700'}`}>
                          {detail.quantity}
                        </td>
                        <td className="py-3 px-4">
                          <Badge 
                            variant={detail.status === 'FULL' ? 'default' : 'outline'}
                            className={`${
                              detail.status === 'FULL' 
                                ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' 
                                : 'bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300'
                            } font-semibold px-2 py-1 text-xs`}
                          >
                            {detail.status}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500 text-lg">No packing details available</p>
              <p className="text-gray-400 text-sm">Packing details will appear here once boxes are created</p>
            </div>
          )}
          
          {/* Invoiced Button */}
          {packingDetails && packingDetails.length > 0 && (
            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
              <Button
                onClick={handleMarkPackingDetailsAsInvoiced}
                disabled={isLoadingPackingDetails || selectedPackingDetails.size === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                {isLoadingPackingDetails ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Invoiced
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Header Invoice Dialog */}
      <Dialog open={isHeaderInvoiceDialogOpen} onOpenChange={setIsHeaderInvoiceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-primary-800 text-lg font-semibold">
              <FileText className="h-5 w-5 mr-3 text-primary-600" />
              Mark as Invoiced
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700 mb-3">
                You are about to mark <span className="font-semibold">{selectedEntries.size}</span> packing header(s) as invoiced.
              </p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="headerInvoiceNumber" className="text-sm font-medium text-gray-700">
                    Invoice Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="headerInvoiceNumber"
                    type="text"
                    value={headerInvoiceNumber}
                    onChange={(e) => setHeaderInvoiceNumber(e.target.value)}
                    placeholder="Enter invoice number"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="headerInvoiceDate" className="text-sm font-medium text-gray-700">
                    Invoice Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="headerInvoiceDate"
                    type="date"
                    value={headerInvoiceDate}
                    onChange={(e) => setHeaderInvoiceDate(e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => {
                setIsHeaderInvoiceDialogOpen(false);
                setHeaderInvoiceNumber('');
                setHeaderInvoiceDate('');
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMarkAsInvoiced}
              disabled={isLoading || !headerInvoiceNumber.trim() || !headerInvoiceDate}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Mark as Invoiced
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackingEntriesPage;
